import { useState, useEffect } from 'react'
import './safetyRules.css'
import { useLanguage } from '../../context/LanguageContext'

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000'
const RULE_ICONS  = ['🪪', '🚧', '👮', '🚨', '📷', '⚠️']

const SafetyRules = () => {
  const { language, tenantSlug, setSafetyRulesReadAt, goNext, setSafetyRulesForPdf, allTranslations } = useLanguage()
  const t = allTranslations[language] || allTranslations['en']

  const [acknowledged, setAcknowledged] = useState(false)
  const [dbRules, setDbRules]           = useState(null)   // rules from DB, null = not loaded yet
  const [display, setDisplay]           = useState('grid') // 'grid' | 'document'
  const [loading, setLoading]           = useState(true)

  useEffect(() => {
    if (!tenantSlug) return

    fetch(`${BACKEND_URL}/api/checkin/safety-rules?lang=${language}`, {
      headers: { 'X-Tenant-Slug': tenantSlug }
    })
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (data && data.rules && data.rules.length > 0) {
          setDbRules(data.rules)
          setDisplay(data.display || 'grid')
        }
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }, [tenantSlug, language])

  // Fetch German rules for PDF (always German regardless of visitor language)
  useEffect(() => {
    if (!tenantSlug) return
    fetch(`${BACKEND_URL}/api/checkin/safety-rules?lang=de`, {
      headers: { 'X-Tenant-Slug': tenantSlug }
    })
      .then(res => res.ok ? res.json() : null)
      .then(data => { if (data?.rules?.length > 0) setSafetyRulesForPdf(data.rules) })
      .catch(() => {})
  }, [tenantSlug])

  const handleContinue = () => {
    setSafetyRulesReadAt(new Date().toISOString())
    goNext()
  }

  // Use DB rules if available, otherwise fall back to translations.json
  const rules = dbRules
    ? dbRules.map((r, idx) => ({ text: r.text, image_url: r.image_url, icon: RULE_ICONS[idx] || '⚠️' }))
    : (t.safety_rules || []).map((text, idx) => ({ text, image_url: null, icon: RULE_ICONS[idx] || '⚠️' }))

  return (
    <div className="viGuard__safetyRules">
      <div className="viGuard__safetyRules-content">
        <h1 className="gradient__text">{t.safety_title}</h1>
        <p className="subtitle">{t.safety_subtitle}</p>

        {loading ? (
          <p className="page-loading-text">{t.loading}</p>
        ) : display === 'document' && dbRules ? (
          <div className="viGuard__safetyRules-document">
            {dbRules.map((rule, idx) => (
              <div className="viGuard__safetyRules-docBlock" key={idx}>
                {rule.image_url && <img src={rule.image_url} alt="" className="viGuard__safetyRules-docImage" />}
                {rule.text && <p className="viGuard__safetyRules-docText">{rule.text}</p>}
              </div>
            ))}
          </div>
        ) : (
          <div className="viGuard__safetyRules-grid">
            {rules.map((rule, idx) => (
              <div className="viGuard__safetyRules-card" key={idx}>
                <div className="viGuard__safetyRules-card__img">
                  {rule.image_url
                    ? <img src={rule.image_url} alt={`rule-${idx}`} className="sr-rule-img" />
                    : <span className="placeholder-icon">{rule.icon}</span>
                  }
                </div>
                <span className="viGuard__safetyRules-card__text">{rule.text}</span>
              </div>
            ))}
          </div>
        )}

        <div className="viGuard__safetyRules-acknowledge">
          <input
            type="checkbox"
            id="acknowledge"
            checked={acknowledged}
            onChange={e => setAcknowledged(e.target.checked)}
          />
          <label htmlFor="acknowledge">{t.safety_acknowledge}</label>
        </div>

        <div className="viGuard__safetyRules-action">
          <button
            type="button"
            disabled={!acknowledged || loading}
            onClick={handleContinue}
          >
            {t.safety_confirm_button}
          </button>
        </div>
      </div>
    </div>
  )
}

export default SafetyRules
